import { computed, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AmlAdminErrorHandlerService } from '@core/services/aml-admin-error-handler.service';
import { EmailCommunicationService } from '@core/services/email-communication.service';
import {
  AvailableEmailStatuses,
  type EmailListTableColumnSortKey,
} from '@features/email-communication/enums/email-list.enums';
import { EmailListTableConfigService } from '@features/email-communication/services/email-list-table-config.service';
import { OrganizationEmailListFilterStore } from '@features/organization/stores/organization-email-list-filter.store';
import type { UUID } from '@kerberos-compliance/compliance-shared-ts/common/types/uuid.type';
import type { RequestFilterEmailCommunicationDto } from '@kerberos-compliance/compliance-shared-ts/core/email/dtos/request.filter-email-communication.dto';
import type { ResponseEmailDto } from '@kerberos-compliance/compliance-shared-ts/features/communication/dtos/response.email.dto';
import { OrganizationEmailTypeMap } from '@kerberos-compliance/compliance-shared-ts/features/notification/enums/email-type.enum';
import type { AmlAdminDeskFeatureTable } from '@kerberos-compliance/compliance-shared-ts/features/user/enums/feature-table.enum';
import { AmlAdminDeskFeatureTableMap } from '@kerberos-compliance/compliance-shared-ts/features/user/enums/feature-table.enum';
import type { ListRequestQueryParameters, TableColumn } from '@kerberos-compliance/compliance-shared-ui';
import { BaseFacadeService, UserTablePreferencesService } from '@kerberos-compliance/compliance-shared-ui';

@Injectable()
export class OrganizationEmailListFacade extends BaseFacadeService<
  ResponseEmailDto,
  RequestFilterEmailCommunicationDto,
  EmailListTableColumnSortKey,
  ListRequestQueryParameters<RequestFilterEmailCommunicationDto>
> {
  protected override readonly componentName = 'OrganizationEmailListFacade';

  protected override readonly filterStore = inject(OrganizationEmailListFilterStore);

  protected override readonly errorHandlerService = inject(AmlAdminErrorHandlerService);

  private readonly emailCommunicationService = inject(EmailCommunicationService);

  private readonly tableConfigService = inject(EmailListTableConfigService);

  private readonly userTablePreferencesService =
    inject<UserTablePreferencesService<ResponseEmailDto, AmlAdminDeskFeatureTable>>(UserTablePreferencesService);

  public readonly tableColumns = computed(() =>
    this.userTablePreferencesService.loadTablePreferences(
      this.tableConfigService.tableColumns(),
      AmlAdminDeskFeatureTableMap.OrganizationEmailNotifications,
    ),
  );

  public readonly emails = this.paginatedData;

  /**
   * Persist the current notifications table configuration for the organization detail view.
   *
   * @param {TableColumn<ResponseEmailDto>[]} columns - Current columns including order and visibility.
   * @returns {Promise<void>} Resolves once the configuration has been saved.
   */
  public async saveTableConfiguration(columns: TableColumn<ResponseEmailDto>[]): Promise<void> {
    await this.userTablePreferencesService.saveTablePreferences(
      AmlAdminDeskFeatureTableMap.OrganizationEmailNotifications,
      columns,
    );
  }

  /**
   * Sets the organization context for subsequent email fetches.
   *
   * @param {UUID} organizationId - The unique identifier of the organization.
   */
  public setOrganizationId(organizationId: UUID): void {
    this.filterStore.setOrganizationId(organizationId);
  }

  /**
   * Loads emails for the current organization context.
   *
   * @returns {Promise<void>} A promise that resolves when the email data loading process is complete.
   */
  public async loadEmails(): Promise<void> {
    await this.fetchData(this.buildFetchParameters());
  }

  /**
   * Sets up the filter subscription and reset the filter listener before the base initialization runs.
   *
   * @returns {void} This method does not return a value.
   */
  protected override onBeforeInitialize(): void {
    this.setupFilterSubscription(this.filterStore.filter$);
    this.filterStore.resetTriggered$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.resetFilters());
  }

  /**
   * Resets all filters and sort state to their default values.
   *
   * @returns {void} This method does not return a value.
   */
  public override resetFilters(): void {
    this.sortService.reset();
    this.sortState.set(undefined);
    this.filterStore.resetFilters();
  }

  /**
   * Prevents data fetching until an organization ID has been set.
   *
   * @param {ListRequestQueryParameters<RequestFilterEmailCommunicationDto>} parameters - The query parameters.
   * @returns {boolean} True when fetching is allowed.
   */
  protected override canFetch(
    parameters: ListRequestQueryParameters<RequestFilterEmailCommunicationDto>,
  ): parameters is ListRequestQueryParameters<RequestFilterEmailCommunicationDto> {
    return super.canFetch(parameters) && this.filterStore.organizationId() !== undefined;
  }

  /**
   * Builds the request filters, defaulting to all available statuses and types when none are selected.
   *
   * @returns {RequestFilterEmailCommunicationDto} The composed filter payload.
   */
  protected override buildFilters(): RequestFilterEmailCommunicationDto {
    const filters = this.filterStore.filterState();

    return {
      ...filters,
      status: filters.status?.length ? filters.status : AvailableEmailStatuses,
      type: filters.type?.length ? filters.type : Object.values(OrganizationEmailTypeMap),
      sentDateStart: filters.sentDateStart ? new Date(filters.sentDateStart) : undefined,
      sentDateEnd: filters.sentDateEnd ? new Date(filters.sentDateEnd) : undefined,
    };
  }

  /**
   * Fetches the email list for the current organization using the provided query parameters.
   *
   * @param {ListRequestQueryParameters<RequestFilterEmailCommunicationDto>} parameters - Query parameters including filters, pagination, and sorting.
   * @returns {Promise<void>} A promise that resolves once the email data is fetched.
   */
  protected override async fetchData(
    parameters: ListRequestQueryParameters<RequestFilterEmailCommunicationDto>,
  ): Promise<void> {
    await this.executeSafely(
      async () => {
        const result = await this.emailCommunicationService.fetchOrganizationEmails(
          this.filterStore.organizationId()!,
          parameters,
        );
        if (result) {
          this.setPaginatedResponse(result, parameters.isLoadingMore);
        }
      },
      `Fetching organization email list for organizationId: ${this.filterStore.organizationId()}`,
      { onError: () => this.replacePaginatedResponse({ data: [], totalCount: 0, hasMore: false }) },
    );
  }
}
